import { forwardRef, useState } from "react"
import { motion, useMotionValueEvent, useTransform } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"

import { buttonContrastMode, buttonReset } from "css/buttons"

import { Icon } from "components/icon/Icon"
import { useRoller } from "./context"
import Button from "components/button/Button"
import { fullGrid } from "css/grid"

const ArrowsContainer = styled(motion.div)`
  ${fullGrid}
  grid-column: doc;
  z-index: 1;
  pointer-events: none;
  height: fit-content;
  grid-area: 1 / 1 / -1 / -1;
`

const Arrow = styled(Button)`
  pointer-events: all;
  &:dir(rtl) svg {
    transform: scale(-1);
  }
`

const Marrow = styled(motion.div)`
  justify-self: center;
  grid-row: 1;

  &.previous {
    justify-self: end;
    grid-column: doc-start;
    margin-inline-end: calc(var(--btn-height) / -2 - var(--grid-gap));
  }
  &.next {
    justify-self: start;
    grid-column: main-end;
    margin-inline-start: calc(var(--btn-height) / -2 - var(--grid-gap));
  }

  &.disabled {
    opacity: 0;
  }

  &:dir(rtl) svg {
    transform: scale(-1);
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover:not(:disabled) {
      cursor: pointer;
    }
  }

  /* ${buttonContrastMode} */
`

function Prev({ scrollX, scroll }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const { dir, unit } = useRoller()

  const disabled = useTransform([scrollX, dir, unit], ([x, d, u]) => x * d < u)

  const [isDisabled, setIsDisabled] = useState(disabled.get())

  function onClick() {
    scroll(-unit.get())
  }

  function onDisable(b) {
    setIsDisabled(b)
  }
  useMotionValueEvent(disabled, "change", onDisable)

  return (
    <Marrow className={`previous${isDisabled ? " disabled" : ""}`} tabIndex={-1}>
      <Arrow
        aria-label={dictionary.previous()}
        aria_hidden={true}
        onClick={onClick}
        tabIndex={-1}
        className='icon translucent-dark'
        icon='movePrevious'
        disabled={isDisabled}
      />
    </Marrow>
  )
}
function Next({ scrollX, scroll }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const { dir, unit, max } = useRoller()

  const disabled = useTransform([scrollX, dir, max], ([x, d, m]) => x * d >= m)

  const [isDisabled, setIsDisabled] = useState(disabled.get())

  function onClick() {
    scroll(unit.get())
  }

  function onDisable(b) {
    setIsDisabled(b)
  }
  useMotionValueEvent(disabled, "change", onDisable)

  return (
    <Marrow className={`next${isDisabled ? " disabled" : ""}`} aria-hidden={true} onClick={onClick} tabIndex={-1} disabled={isDisabled ? "disabled" : ""}>
      <Arrow
        aria-label={dictionary.next()}
        aria_hidden={true}
        onClick={onClick}
        tabIndex={-1}
        className='icon translucent-dark'
        icon='moveNext'
        disabled={isDisabled}
      />
    </Marrow>
  )
}

function Arrows({ scrollX }, ref) {
  const dictionary = useDictionary()

  const { dir } = useRoller()

  const scroll = v => {
    ref.current.scrollBy({
      top: 0,
      left: v * dir.get(),
      behavior: "smooth",
    })
  }

  return (
    <ArrowsContainer className='dark-theme'>
      <Prev scrollX={scrollX} scroll={scroll} />
      <Next scrollX={scrollX} scroll={scroll} />
    </ArrowsContainer>
  )
}

export default forwardRef(Arrows)
